'use client'

import type { PropsWithChildren } from 'react'
import { Suspense, useMemo, useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Moon, Sun, LayoutGrid, Headphones, LogOut, CreditCard } from 'lucide-react'
import type { AppLocale } from '@/lib/i18n/locales'
import { routeMeta } from '@/client/routes/routeMeta'
import { useAppStore } from '@/client/store/appStore'
import { Sidebar as ShadSidebar, SidebarBody, SidebarLink, useSidebar } from '@/components/ui/sidebar'
import { useTranslation } from 'react-i18next'
import { clearStoredSystemToken, isApiConfigured, setStoredApiKey } from '@/client/utils/apiConfig'
import { useUserSelfStore, refreshUserSelf } from '@/client/store/userSelfStore'
import { LanguageSwitcher } from '@/client/components/LanguageSwitcher'
import useBootstrapClient from '@/features/hooks/useBootstrapClient'
import { getStoredUser, hasSessionCookie, USER_STORAGE_KEY, clearStoredUser, type StoredUser } from '@/client/auth/userStorage'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle as DialogTitleShad } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import AnnouncementBanner from '@/client/components/AnnouncementBanner'
import LoginPanel from '@/features/auth/LoginPanel'
import { cn } from '@/lib/utils'
import { autoConfigureApiKey } from '@/client/api/tokens'

const resolveHref = (locale: AppLocale, path: string): string => {
  if (!path || path === '/') {
    return `/${locale}/create`
  }
  if (path === '/create') {
    return `/${locale}/create`
  }
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `/${locale}${normalized}`
}

// 顶部 API 配置提示（shadcn + Tailwind）
interface ApiConfigNoticeProps {
  locale: AppLocale
  onRequireLogin?: () => void
}

const ApiConfigNotice: React.FC<ApiConfigNoticeProps> = ({ locale, onRequireLogin }) => {
  const [show, setShow] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [autoConfigRunning, setAutoConfigRunning] = useState(false)
  const { t } = useTranslation(['components', 'common'])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const user = getStoredUser()
    const hasUser = !!user
    setIsLoggedIn(hasUser)
    if (!hasUser) {
      setShow(true)
      return
    }
    const configured = isApiConfigured()
    setShow(!configured)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!isLoggedIn) return
    if (isApiConfigured()) {
      setShow(false)
      return
    }

    let cancelled = false
    setAutoConfigRunning(true)

    const run = async () => {
      try {
        await autoConfigureApiKey()
      } catch (error) {
        console.warn('ApiConfigNotice: 自动配置 API Key 失败', error)
      } finally {
        if (cancelled) return
        setAutoConfigRunning(false)
        if (isApiConfigured()) {
          setShow(false)
        }
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [isLoggedIn])

  if (!show) return null

  const configured = isApiConfigured()
  const needsConfig = isLoggedIn && !configured

  return (
    <div className="px-3 py-2">
      <Alert className="mb-2">
        <AlertTitle className="font-semibold">
          {t('components:apiConfigNotice.title')}
        </AlertTitle>
        <AlertDescription>
          {!isLoggedIn ? (
            <div className="flex items-center justify-between gap-2">
              <span>请登录以使用服务</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (onRequireLogin) {
                    onRequireLogin()
                  } else {
                    try {
                      window.location.href = `/${locale}/create`
                    } catch (error) {
                      console.warn('ApiConfigNotice: 跳转登录失败', error)
                    }
                  }
                }}
              >
                登录
              </Button>
            </div>
          ) : needsConfig ? (
            <div className="flex items-center justify-between gap-2">
              <span>{autoConfigRunning ? '正在配置 API Key...' : t('components:apiConfigNotice.description')}</span>
              {!autoConfigRunning && (
                <Button asChild variant="outline" size="sm">
                  <Link href={`/${locale}/settings`}>{t('common:actions.gotoSettings')}</Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <span>{t('components:apiConfigNotice.description')}</span>
              <Button asChild variant="outline" size="sm">
                <Link href={`/${locale}/settings`}>{t('common:actions.gotoSettings')}</Link>
              </Button>
            </div>
          )}
        </AlertDescription>
      </Alert>
    </div>
  )
}

// 侧边栏（shadcn）
interface SidebarProps {
  locale: AppLocale
  isAuthenticated: boolean
  onRequireLogin: (href: string) => void
  user?: StoredUser | null
  onLogout?: () => void
}

const AppSidebarContent: React.FC<SidebarProps> = ({ locale, isAuthenticated, onRequireLogin, user, onLogout }) => {
  const { t } = useTranslation('layout')
  const { open: sidebarOpen } = useSidebar()
  const createHref = resolveHref(locale, '/create')
  const allowedWhenGuest = useMemo(() => new Set<string>(['/dashboard', '/help', '/settings']), [])
  // 使用全局 routeMeta（已迁移到 lucide 图标）
  // 上半部分路由（分隔线前）：canvas, storyboard, batch
  const topRoutes = useMemo(
    () => routeMeta.filter((route) => ['/canvas', '/storyboard', '/batch'].includes(route.path)),
    []
  )
  // 下半部分路由（分隔线后）：dashboard, history, gallery, settings, help
  const bottomRoutes = useMemo(
    () => routeMeta.filter((route) => !route.hidden && !['/canvas', '/storyboard', '/batch'].includes(route.path)),
    []
  )
  const themeMode = useAppStore((state) => state.themeMode)
  const setThemeMode = useAppStore((state) => state.setThemeMode)
  const displayName = user?.display_name || user?.username || ''
  const dollarLabel = useUserSelfStore((s) => s.dollarLabel)

  const toggleTheme = () => setThemeMode(themeMode === 'light' ? 'dark' : 'light')

  const handleCopy = useCallback(async (text: string, successMsg: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const input = document.createElement('input')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }
      toast.success(successMsg)
    } catch {
      toast.error('复制失败')
    }
  }, [])

  const handleCopyBrand = useCallback(() => handleCopy('art.wyjh.top', '已复制网站地址到剪贴板'), [handleCopy])
  const handleCopyWechat = useCallback(() => handleCopy('Tian_Riching', '已复制客服微信号'), [handleCopy])

  const makeOnClick = (path: string, href: string) => (event: React.MouseEvent) => {
    if (!isAuthenticated && !allowedWhenGuest.has(path)) {
      event.preventDefault()
      onRequireLogin(href)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* 顶部品牌区 */}
      <div className="px-1 pb-2">
        <div
          onClick={handleCopyBrand}
          aria-label="点击复制 art.wyjh.top"
          className="relative h-6 overflow-hidden text-base font-bold cursor-pointer select-none"
        >
          <span
            className={cn(
              'absolute inset-0 flex items-center transition-all duration-200 transform-gpu',
              sidebarOpen ? 'opacity-0 translate-y-1 pointer-events-none' : 'opacity-100 translate-y-0'
            )}
          >
            AI
          </span>
          <span
            className={cn(
              'absolute inset-0 flex items-center transition-all duration-200 transform-gpu',
              sidebarOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'
            )}
          >
            {t('nav.brand')}
          </span>
        </div>
      </div>

      {/* 导航区 */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-1">
          {/* 上半部分：画布 */}
          {topRoutes.filter((route) => route.path === '/canvas').map((route) => {
            const href = resolveHref(locale, route.path)
            const label = t(`layout:${route.labelKey}`)
            const Icon = route.icon
            return (
              <SidebarLink
                key={route.path}
                link={{ label, href, icon: Icon ? <Icon className="size-4" /> : null }}
                onClick={makeOnClick(route.path, href)}
              />
            )
          })}
          {/* 创作 */}
          <SidebarLink
            link={{ label: t('nav.routes.create'), href: createHref, icon: <LayoutGrid className="size-4" /> }}
            onClick={makeOnClick('/create', createHref)}
          />
          {/* 故事板、批量任务 */}
          {topRoutes.filter((route) => route.path !== '/canvas').map((route) => {
            const href = resolveHref(locale, route.path)
            const label = t(`layout:${route.labelKey}`)
            const Icon = route.icon
            return (
              <SidebarLink
                key={route.path}
                link={{ label, href, icon: Icon ? <Icon className="size-4" /> : null }}
                onClick={makeOnClick(route.path, href)}
              />
            )
          })}
          <div className="my-2 h-px bg-[var(--sidebar-border)]" />
          {/* 下半部分：仪表盘、历史记录、图库、设置、帮助 */}
          {bottomRoutes.map((route) => {
            const href = resolveHref(locale, route.path)
            const label = t(`layout:${route.labelKey}`)
            const Icon = route.icon
            return (
              <SidebarLink
                key={route.path}
                link={{ label, href, icon: Icon ? <Icon className="size-4" /> : null }}
                onClick={makeOnClick(route.path, href)}
              />
            )
          })}
        </div>
      </div>

      {/* 底部账户与设置区 */}
      <div className="relative mt-2 border-t pt-2 min-h-[180px] overflow-hidden will-change-transform transform-gpu">
        {/* 展开状态层 */}
        <div
          className={cn(
            'absolute inset-x-0 top-0 bottom-8 flex flex-col gap-2 transition-all duration-200 transform-gpu',
            sidebarOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
          )}
        >
          <div className="flex items-center justify-between gap-2 overflow-hidden min-h-[20px]">
            {displayName && <div className="text-sm font-semibold truncate">{displayName}</div>}
            <div className="text-sm opacity-90 text-muted-foreground">{dollarLabel}</div>
          </div>
          <div className="mt-2 space-y-2">
            <div>
              <Button variant="outline" size="sm" onClick={handleCopyWechat} className="w-full font-semibold">
                添加客服进群
              </Button>
            </div>
            <div>
              <Button
                variant="default"
                size="sm"
                className="w-full font-semibold"
                onClick={() => window.open('https://www.wyjh.top/console/topup', '_blank', 'noopener,noreferrer')}
              >
                <CreditCard className="size-4 mr-2" />
                充值
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('layout:nav.toggleThemeAria')}
                    onClick={toggleTheme}
                  >
                    {themeMode === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {themeMode === 'light' ? t('layout:nav.toggleThemeDark') : t('layout:nav.toggleThemeLight')}
                </TooltipContent>
              </Tooltip>
              <Suspense fallback={null}>
                <LanguageSwitcher />
              </Suspense>
              {onLogout && (
                <Button variant="ghost" size="sm" onClick={onLogout} className="ml-auto">
                  退出登录
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* 收起状态层 */}
        <div
          className={cn(
            'absolute inset-x-0 top-0 bottom-8 flex flex-col items-center justify-center gap-2 transition-all duration-200 transform-gpu',
            sidebarOpen ? 'opacity-0 -translate-y-2 pointer-events-none' : 'opacity-100 translate-y-0 pointer-events-auto'
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={handleCopyWechat} aria-label="添加客服进群">
                <Headphones className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>添加客服进群</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => window.open('https://www.wyjh.top/console/topup', '_blank', 'noopener,noreferrer')}
                aria-label="充值"
              >
                <CreditCard className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>充值</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={toggleTheme} aria-label={t('layout:nav.toggleThemeAria')}>
                {themeMode === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {themeMode === 'light' ? t('layout:nav.toggleThemeDark') : t('layout:nav.toggleThemeLight')}
            </TooltipContent>
          </Tooltip>
          {onLogout && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onLogout} aria-label="退出登录">
                  <LogOut className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>退出登录</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* 固定在底部的版本号 */}
        <div className="absolute inset-x-0 bottom-0 py-2 text-center text-xs opacity-60">
          {t('sidebar.version', { version: '0.1' })}
        </div>
      </div>
    </div>
  )
}

const AppSidebar: React.FC<SidebarProps> = (props) => {
  return (
    <ShadSidebar>
      <SidebarBody>
        <AppSidebarContent {...props} />
      </SidebarBody>
    </ShadSidebar>
  )
}

interface AppShellProps extends PropsWithChildren {
  locale: AppLocale
}

const AppShell: React.FC<AppShellProps> = ({ locale, children }) => {
  useBootstrapClient()
  const router = useRouter()
  const [authState, setAuthState] = useState<'checking' | 'login' | 'authenticated'>('checking')
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(null)
  const [loginOpen, setLoginOpen] = useState(false)
  const [pendingHref, setPendingHref] = useState<string | null>(null)
  const userIdForRefresh = currentUser?.id

  const evaluateAuth = useCallback(() => {
    const stored = getStoredUser()
    const hasSession = hasSessionCookie()
    setCurrentUser(stored)
    if (!stored && !hasSession) {
      setAuthState('login')
    } else {
      setAuthState('authenticated')
    }
  }, [])

  useEffect(() => {
    evaluateAuth()
    if (typeof window === 'undefined') return
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== USER_STORAGE_KEY) return
      evaluateAuth()
    }
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [evaluateAuth])

  useEffect(() => {
    if (authState === 'authenticated' && userIdForRefresh) {
      void refreshUserSelf()
    }
  }, [authState, userIdForRefresh])

  const handleLoginSuccess = useCallback(
    (user: StoredUser | null) => {
      if (!user) {
        setAuthState('login')
        return
      }
      setCurrentUser(user)
      setAuthState('authenticated')
      if (pendingHref) {
        try {
          router.push(pendingHref)
        } catch (err) {
          console.warn('AppShell: 登录后跳转失败', err)
        }
        setPendingHref(null)
      }
      setLoginOpen(false)
      setTimeout(() => {
        evaluateAuth()
      }, 0)
    },
    [evaluateAuth, pendingHref, router]
  )

  const handleLogout = useCallback(() => {
    clearStoredUser()
    if (typeof window !== 'undefined') {
      try {
        setStoredApiKey('')
        clearStoredSystemToken()
      } catch (error) {
        console.warn('AppShell: 清理 API Key 失败', error)
      }
      try {
        const cookies = typeof document !== 'undefined' && document.cookie ? document.cookie.split(';') : []
        const hostname = window.location.hostname
        const domainParts = hostname.split('.').filter(Boolean)
        const rootDomain = domainParts.length >= 2 ? `.${domainParts.slice(-2).join('.')}` : null
        cookies.forEach((cookie) => {
          const eqIndex = cookie.indexOf('=')
          const name = (eqIndex > -1 ? cookie.substring(0, eqIndex) : cookie).trim()
          if (!name) return
          document.cookie = `${name}=; Max-Age=0; path=/;`
          if (rootDomain) {
            document.cookie = `${name}=; Max-Age=0; path=/; domain=${rootDomain}`
          }
        })
      } catch (error) {
        console.warn('AppShell: 清理 Cookie 失败', error)
      }
    }
    setCurrentUser(null)
    setAuthState('login')
    setTimeout(() => {
      evaluateAuth()
    }, 0)
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [evaluateAuth])

  const requestLogin = useCallback((href: string) => {
    setPendingHref(href)
    setLoginOpen(true)
  }, [])

  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="size-8 border-2 border-muted-foreground/20 border-t-primary rounded-full animate-spin" aria-label="loading" />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <Toaster />
      <div className="shrink-0">
        <ApiConfigNotice
          locale={locale}
          onRequireLogin={() => {
            requestLogin('/create')
          }}
        />
        <AnnouncementBanner />
      </div>

      <div className="flex flex-1 min-h-0 w-full">
        <AppSidebar
          locale={locale}
          isAuthenticated={authState === 'authenticated'}
          onRequireLogin={requestLogin}
          user={currentUser}
          onLogout={authState === 'authenticated' ? handleLogout : undefined}
        />
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden max-w-full flex flex-col">
            {children}
          </div>
        </main>
      </div>

      {/* 登录弹窗（shadcn Dialog） */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitleShad>请登录以使用该功能</DialogTitleShad>
          </DialogHeader>
          <LoginPanel onLoginSuccess={handleLoginSuccess} />
          <div className="mt-2 text-center text-xs text-muted-foreground">使用 "www.wyjh.top" 账号登录，未注册可前往官网注册。</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginOpen(false)}>
              取消
            </Button>
            <Button variant="secondary" onClick={() => window.open('https://www.wyjh.top', '_blank', 'noopener,noreferrer')}>
              前往注册
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AppShell
